import * as Sentry from '@sentry/react';
import { AUTH0_CONFIG, SENTRY_CONFIG, ELEOS_ENV } from '../Utils/config';

import { useTheme } from '@mui/material/styles';
import React, { useMemo } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { EleosLogin, ProtectedRoute } from '@common-ui-library/eleos-login';
import { Dashboard } from '@common-ui-library/dashboard';
import { Backdrop } from '@common-ui-library/molecule';
import type { AppProviderProps, Router } from '@common-ui-library/dashboard';
import { EleosLogo } from '@common-ui-library/molecule';
import {
  ThemeProviderWrapper,
  logger,
} from '@common-ui-library/shared';

import { BASE_URL } from '../Utils/utils';
import { SessionContextProvider, useSessionContext } from './SessionContext';
import { UnauthorizedPage } from './UnauthorizedPage';
import { AuthErrorInterceptor } from './AuthErrorInterceptor';
import { GenericErrorPage } from './GenericErrorPage';
import { NotFoundPage } from './NotFoundPage';
import { UserManagementProvider } from '../UserManagement/UserManagementProvider';
import {
  UserManagementDashboardContent,
  useUserManagementNavigation,
} from '../UserManagement/routes';

Sentry.init({
  dsn: SENTRY_CONFIG.dsn,
  environment: ELEOS_ENV,
  integrations: [
    Sentry.captureConsoleIntegration({
      levels: ['error', 'warn'],
    }),
  ],
});

const AdminPortalContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const navigation = useUserManagementNavigation();

  const { session, userManagementClient } = useSessionContext();

  const router = useMemo<Router>(
    () => ({
      pathname: location.pathname,
      searchParams: new URLSearchParams(location.search),
      navigate: (
        url: string | URL,
        options?: { history?: 'auto' | 'push' | 'replace' }
      ) => {
        navigate(url, { replace: options?.history === 'replace' });
      },
    }),
    [location.pathname, location.search]
  );

  const appProviderProps: Partial<AppProviderProps> = {
    session: session,
    navigation,
    router,
    theme: theme as any, // Cast theme to avoid type mismatch between MUI versions
    branding: {
      title: 'Eleos Management Hub',
      logo: <EleosLogo />,
      homeUrl: BASE_URL,
    },
  };

  if (!session || !userManagementClient) {
    return null;
  }

  return (
    <Dashboard
      appProviderProps={appProviderProps}
      dashboardLayoutProps={{
        disableCollapsibleSidebar: false,
        sidebarExpandedWidth: '15%',
        hideNavigation: false,
      }}
    >
      <ThemeProviderWrapper>
        <UserManagementProvider
          client={userManagementClient}
          session={session as any} // Cast to match generic interface
        >
          <UserManagementDashboardContent />
        </UserManagementProvider>
      </ThemeProviderWrapper>
    </Dashboard>
  );
};

const EleosAdminPortalApp = () => {

  const { authError, isPermissionChecking } = useSessionContext();

  if (isPermissionChecking) {
    return (
      <Backdrop open={true} title="Loading..."/>
    );
  }

  if (authError) {
    return <UnauthorizedPage />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to="/admin" replace />}
      />
      <Route
        path="/admin/*"
        element={<AdminPortalContent />}
      />
      <Route
        path="*"
        element={<NotFoundPage fullPage />}
      />
    </Routes>
  );
};

const EleosAdminPortalAppWrapper = () => {
  logger.info(`Eleos Management Hub`);

  const handleLogin = (user: any) => {
    logger.info('User logged in:', user);
  };

  const handleLogout = () => {
    logger.info('User logged out');
  };

  return (
    <Sentry.ErrorBoundary
      fallback={({ eventId }) => <GenericErrorPage eventId={eventId} />}
      showDialog={false}
    >
      <EleosLogin
        domain={AUTH0_CONFIG.domain}
        clientId={AUTH0_CONFIG.clientId}
        audience={AUTH0_CONFIG.audience}
        scope={AUTH0_CONFIG.scope}
        redirectUri={AUTH0_CONFIG.redirectUri}
        onLogin={handleLogin}
        onLogout={handleLogout}
      >
        <AuthErrorInterceptor>
          <ProtectedRoute>
            <SessionContextProvider>
              <EleosAdminPortalApp />
            </SessionContextProvider>
          </ProtectedRoute>
        </AuthErrorInterceptor>
      </EleosLogin>
    </Sentry.ErrorBoundary>
  );
};

export default EleosAdminPortalAppWrapper;
